/** @fileoverview Handles Platform session auth and authenticated Fabrik API requests. */

import { APP_NAME, LOGIN_METHOD_KEY, SESSION_INFO_COOKIE_NAMES } from '../constants';
import type { User } from '../types/identity';

const getDomainEnvFromHostname = () => {
  const subdomain = window.location.hostname.split('.').shift() ?? '';
  return subdomain.replace(APP_NAME, '');
};

const getAppEnv = () => {
  const envWithDashPrefixed = getDomainEnvFromHostname();
  if (envWithDashPrefixed) {
    if (envWithDashPrefixed === 'localhost') return 'localhost';
    return envWithDashPrefixed.slice(1);
  }
  return 'production';
};

const getAPIBaseURL = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:2000';
  }
  return `https://platform${getDomainEnvFromHostname()}-api.integrtr.com`;
};

const apiBase = () => getAPIBaseURL().replace(/\/$/, '');

const getPlatformURL = () => {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_LOCAL_PLATFORM_URI || 'http://localhost:8080';
  }
  return `https://platform${getDomainEnvFromHostname()}.integrtr.com`;
};

const platformBase = () => getPlatformURL().replace(/\/$/, '');

/** Builds the Platform launchpad URL used by the Dataflow logo. */
export const getPlatformHomeURL = (tenantId?: string) =>
  `${platformBase()}/#/launchpad/${tenantId ? `t-${tenantId}-t` : ''}`;

/** Builds the Platform Systems detail URL for one connected user system. */
export const getPlatformSystemURL = (tenantId: string, userSystemId: string) =>
  `${platformBase()}/#/systems/t-${encodeURIComponent(tenantId)}-t&/s/system/${encodeURIComponent(
    userSystemId
  )}`;

/** Resolves the shared Help host using the same environment rule as Dataflow. */
export const getHelpURL = () => {
  const env = getAppEnv();
  if (env === 'localhost' || env === 'dev') return 'https://help-staging.integrtr.com';
  if (env === 'staging') return `https://help-${env}.integrtr.com`;
  return 'https://help.integrtr.com';
};

const getSessionInfoCookieName = () => {
  const envMap: Record<string, string> = {
    localhost: SESSION_INFO_COOKIE_NAMES.LOCAL,
    dev: SESSION_INFO_COOKIE_NAMES.DEV,
    staging: SESSION_INFO_COOKIE_NAMES.STAGING,
    sandbox: SESSION_INFO_COOKIE_NAMES.SANDBOX,
    production: SESSION_INFO_COOKIE_NAMES.PROD,
    experiment: SESSION_INFO_COOKIE_NAMES.EXPT,
  };
  return envMap[getAppEnv()] || SESSION_INFO_COOKIE_NAMES.PROD;
};

/** Reads and decodes a named browser cookie when available. */
function cookie(name: string) {
  const value = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`))
    ?.slice(name.length + 1);
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

const token = () =>
  cookie('basicToken') ||
  cookie('ssoToken') ||
  (import.meta.env.DEV ? import.meta.env.VITE_LOCAL_AUTH_TOKEN || '' : '');

const readSessionInfo = (): Record<string, unknown> => {
  try {
    return JSON.parse(cookie(getSessionInfoCookieName()) || '{}');
  } catch {
    return {};
  }
};

const refreshToken = () => {
  const value = readSessionInfo().refreshToken;
  return typeof value === 'string' ? value : '';
};

const writeSessionInfo = (sessionInfo: Record<string, unknown>) => {
  const domain = window.location.hostname.endsWith('.integrtr.com') ? '; domain=.integrtr.com' : '';
  document.cookie = `${getSessionInfoCookieName()}=${encodeURIComponent(
    JSON.stringify(sessionInfo)
  )}; path=/${domain}`;
};

const getJwtExpiresAt = (value: string) => {
  try {
    const payload = value.split('.')[1];
    if (!payload) return 0;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const expiresAt = Number((JSON.parse(decoded) as { exp?: number }).exp) * 1000;
    return Number.isFinite(expiresAt) ? expiresAt : 0;
  } catch {
    return 0;
  }
};

/** Returns the access token expiry used to show the session warning. */
export const getPlatformSessionExpiresAt = () => {
  const expiresAt = Number(readSessionInfo().tokenExpiryTime);
  return getJwtExpiresAt(token()) || (Number.isFinite(expiresAt) ? expiresAt : 0);
};

const clearCookie = (name: string) => {
  const domain = window.location.hostname.endsWith('.integrtr.com') ? '; domain=.integrtr.com' : '';
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
};

/** Redirects the browser to Platform sign-in with the current page as the return URL. */
export const login = () => {
  const loginPath = import.meta.env.DEV ? '/login.html' : '/login';
  window.location.replace(
    `${platformBase()}${loginPath}?redirectTo=${encodeURIComponent(window.location.href)}`
  );
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshing: Promise<void> | undefined;

/** Refreshes the Platform session once, sharing an in-flight refresh across callers. */
async function refresh(tenantId?: string) {
  if (!refreshing) {
    refreshing = (async () => {
      const headers: Record<string, string> = {};
      if (tenantId) headers['X-TenantId'] = tenantId;
      if (token()) headers.Authorization = `Bearer ${token()}`;

      const currentRefreshToken = refreshToken();
      if (currentRefreshToken) headers['x-refresh-token'] = currentRefreshToken;

      const loginMethod = localStorage.getItem(LOGIN_METHOD_KEY) || '';
      const refreshType =
        loginMethod === 'SSO' || cookie('ssoToken') ? 'cognito_token' : 'auth0_token';
      const response = await fetch(`${apiBase()}/api/refresh?refresh_type=${refreshType}`, {
        credentials: 'include',
        headers,
      });
      if (!response.ok || !token()) {
        login();
        throw new Error('Please sign in to continue.');
      }
      const data = await response.json().catch(() => ({}));
      if (typeof data.refreshToken === 'string' && data.refreshToken) {
        writeSessionInfo({ ...readSessionInfo(), refreshToken: data.refreshToken });
      }
      window.dispatchEvent(new Event('platform-session-refreshed'));
    })().finally(() => {
      refreshing = undefined;
    });
  }
  return refreshing;
}

/** Refreshes the signed-in Platform session through Core's standard refresh endpoint. */
export const refreshPlatformSession = () => refresh();

/** Sends an authenticated Core API request and maps common failures to ApiError. */
export async function request(path: string, tenantId?: string, options: RequestInit = {}) {
  if (!token()) await refresh(tenantId);

  const send = () => {
    const headers = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(tenantId ? { 'X-TenantId': tenantId } : {}),
    });
    new Headers(options.headers).forEach((value, name) => headers.set(name, value));
    return fetch(`${apiBase()}/api${path}`, { ...options, credentials: 'include', headers });
  };

  let response = await send();
  if (response.status === 401) {
    await refresh(tenantId);
    response = await send();
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    if (response.status === 401) login();
    const message = [
      'FABRIK_PLAN_REQUIRED',
      'SHARED_AI_PLAN_REQUIRED',
      'FABRIK_AI_QUOTA_EXHAUSTED',
    ].includes(body.code)
      ? body.message
      : response.status === 429
      ? `Too many requests. Try again in ${response.headers.get('Retry-After') || '60'} seconds.`
      : response.status === 402
      ? 'Your assistant allowance is exhausted. Contact your tenant administrator.'
      : response.status === 403
      ? 'You do not have permission to use Fabrik in this tenant.'
      : (typeof body.msg === 'string' && !body.msg.startsWith('i18n__') && body.msg) ||
        (typeof body.errorDetails === 'string' && body.errorDetails) ||
        body.message ||
        (response.status >= 500
          ? 'Fabrik could not complete this request. Please try again.'
          : 'The request could not be completed.');
    throw new ApiError(
      typeof message === 'string' ? message : 'The request could not be completed.',
      typeof body.code === 'string' ? body.code : undefined,
      response.status
    );
  }
  return response;
}

/** Sends an authenticated request and parses its JSON response. */
export async function json<T>(
  path: string,
  tenantId?: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await request(path, tenantId, options);
  return response.status === 204 ? (undefined as T) : response.json();
}

/** Loads the signed-in Platform user and their tenant permissions. */
export const getCurrentUser = (signal?: AbortSignal) =>
  json<User>('/whoAmI', undefined, { signal });

/** Ends the Platform session locally and server-side, then returns to sign-in. */
export const logout = async () => {
  const currentToken = token();
  const currentRefreshToken = refreshToken();

  try {
    await fetch(`${apiBase()}/api/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
        ...(currentRefreshToken ? { 'x-refresh-token': currentRefreshToken } : {}),
      },
    });
  } finally {
    clearCookie('basicToken');
    clearCookie('ssoToken');
    clearCookie(getSessionInfoCookieName());
    localStorage.removeItem(LOGIN_METHOD_KEY);
    login();
  }
};
